import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What is fMRI-guided TMS?",
    answer:
      "fMRI-guided TMS uses functional magnetic resonance imaging to compute an individualized stimulation target for each patient, rather than using scalp landmarks. SAINT® uses this approach: the target is selected from how your prefrontal cortex connects to deeper mood circuits, based on your own brain scan.",
  },
  {
    question: "Why does brain targeting matter?",
    answer:
      "The 'right' spot for stimulation varies from brain to brain. Conventional TMS uses scalp landmarks — a population average. fMRI-guided targeting computes your individual target from your brain's functional connectivity, which may improve precision. This is a key differentiator of SAINT®.",
  },
  {
    question: "What is functional connectivity?",
    answer:
      "Functional connectivity refers to how different brain regions communicate — which areas activate together. SAINT® uses resting-state fMRI to measure functional connectivity between your prefrontal cortex and deeper mood circuits, then computes the stimulation target that best connects to those circuits.",
  },
  {
    question: "How does SAINT® incorporate individualized targeting?",
    answer:
      "Before treatment, you undergo a structural and functional MRI. A proprietary algorithm analyzes your brain's functional connectivity and selects the stimulation target in the left dorsolateral prefrontal cortex that best connects to the subgenual anterior cingulate cortex — a region involved in mood regulation.",
  },
  {
    question:
      "How is fMRI targeting different from conventional TMS targeting?",
    answer:
      "Conventional TMS targets the left dorsolateral prefrontal cortex using scalp measurements from the motor-threshold location — often the '5.5 cm rule.' fMRI-guided targeting replaces the ruler with a brain scan and an algorithm that computes your individual target from your functional connectivity.",
  },
  {
    question: "Do I need an MRI for fMRI-guided TMS?",
    answer:
      "Yes. SAINT® requires a structural and functional MRI before treatment. Standard MRI safety screening applies. People who cannot safely have an MRI — due to metal implants, cochlear implants, aneurysm clips, or severe claustrophobia — are not candidates for SAINT®.",
  },
];

export const pageFmriGuided: LearnPageData = {
  slug: "fmri-guided-tms-for-depression",
  title: "fMRI-Guided TMS: When It's Needed and How It's Used | Neuro Wellness",
  description:
    "When is fMRI needed before TMS, how is it used, and is fMRI-guided TMS the same as SAINT\u00ae? Does insurance cover the MRI for SAINT\u00ae? Plain answers and conditional benefits-check wording.",
  canonical: "/learn/fmri-guided-tms-for-depression",
  h1: "fMRI-Guided TMS: When It's Needed and How It's Used",
  breadcrumbLabel: "fMRI-Guided TMS",
  crumbs: baseCrumbs("fMRI-Guided TMS"),
  shortAnswer:
    "fMRI-guided TMS uses functional magnetic resonance imaging to compute an individualized stimulation target for each patient, rather than relying on scalp landmarks used in conventional TMS. SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) incorporates fMRI-guided targeting: before treatment, you undergo a brain scan, and a proprietary algorithm selects the stimulation site based on how your prefrontal cortex connects to deeper mood circuits. Standard TMS does not require an MRI, and an fMRI by itself does not establish that a program is SAINT\u00ae. This page explains what fMRI is, when it's needed, and how SAINT\u00ae uses it.",
  keyTakeaways: [
    "fMRI-guided TMS computes an individualized target from your brain scan.",
    "Conventional TMS uses scalp landmarks \u2014 a population average, and does not require an MRI.",
    "SAINT\u00ae uses fMRI to select the stimulation site, but an fMRI alone does not make a program SAINT\u00ae.",
    "Functional connectivity measures how brain regions communicate.",
    "An MRI is required for SAINT\u00ae \u2014 people who cannot have one are not candidates.",
  ],

  sections: [
    {
      heading: "When is fMRI needed before TMS?",
      body: (
        <>
          {P(
            "fMRI is not required for conventional TMS. Standard TMS targets the left dorsolateral prefrontal cortex using scalp measurements \u2014 typically measuring from the spot that makes the thumb twitch (the motor threshold location) and moving about 5.5 cm forward. That works without any brain scan, and it is the method most TMS clinics use. An fMRI is only needed when the protocol calls for personalized, connectivity-based targeting \u2014 which is the case for SAINT\u00ae.",
          )}
          {P(
            "So the honest answer to \u2018When is an fMRI needed before TMS?\u2019 is: only when you are pursuing a connectivity-guided protocol like SAINT\u00ae. If a clinic offers \u2018accelerated TMS\u2019 or a \u20185-day protocol\u2019 without a brain MRI, that is not the fMRI-guided SAINT\u00ae protocol. [[BEAM F3 vs fMRI targeting|/learn/beam-f3-vs-fmri-tms]].",
          )}
        </>
      ),
    },
    {
      heading: "How is fMRI used in TMS?",
      body: (
        <>
          {P(
            "In SAINT\u00ae, you undergo a structural MRI (a detailed 3D map of your brain's anatomy) and a resting-state functional MRI (fMRI), which measures how different brain regions communicate while you lie still. No injections or dyes are involved. A proprietary algorithm analyzes the fMRI to find the precise spot in the left dorsolateral prefrontal cortex whose activity is most strongly anticorrelated with the subgenual anterior cingulate cortex \u2014 a deeper region involved in mood regulation. That spot becomes your personalized target.",
          )}
          {P(
            "Conventional TMS does not use this scan; it targets the DLPFC with scalp measurements alone. The fMRI is what lets SAINT\u00ae personalize the target to your brain rather than a population average.",
          )}
        </>
      ),
    },
    {
      heading: "Is fMRI-guided TMS the same as SAINT\u00ae?",
      body: (
        <>
          {P(
            "Not necessarily. An fMRI is one component of SAINT\u00ae, but SAINT\u00ae is a complete system: the FDA-cleared Magnus Neuromodulation System, its proprietary targeting software, the fMRI-guided target, the intermittent theta burst schedule (10 sessions/day for 5 days), and Magnus-trained staff. A clinic could in principle use an fMRI for targeting without delivering the full SAINT\u00ae protocol or using the Magnus system.",
          )}
          {P(
            "If a clinic says it uses fMRI but cannot confirm it runs the Magnus Neuromodulation System with SAINT Technology, it is not delivering SAINT\u00ae. The 5-check test \u2014 Magnus provider locator listing, brain MRI for targeting, Magnus system/software, FDA-cleared 5-day protocol, and adults-only \u2014 is the way to tell. [[What is a verified SAINT\u00ae provider?|/learn/what-is-a-verified-saint-provider]].",
          )}
        </>
      ),
    },
    {
      heading: "Does insurance cover the MRI for SAINT\u00ae?",
      body: (
        <>
          {P(
            "SAINT\u00ae coverage is limited and plan-specific. The MRI required for SAINT\u00ae may or may not be covered by your plan, and prior authorization is often involved. We do not promise coverage \u2014 we verify your benefits for free so you know what your plan covers before you commit. [[Check your insurance benefits \u2014 free|/check-insurance-options]] or call 954-827-9793.",
          )}
        </>
      ),
    },
    {
      heading: "What fMRI Is",
      body: (
        <>
          {P(
            "Functional magnetic resonance imaging (fMRI) measures brain activity by detecting changes in blood flow. Unlike a structural MRI, which shows anatomy, fMRI shows which brain regions are active and how they communicate. In SAINT\u00ae, a resting-state fMRI is used \u2014 you simply lie still in the scanner; no task is required.",
          )}
        </>
      ),
    },
    {
      heading: "Why Brain Targeting Matters",
      body: (
        <>
          {P(
            "The 'right' spot for TMS stimulation varies from brain to brain. Conventional TMS targets the left dorsolateral prefrontal cortex using scalp landmarks \u2014 a population average that may not be optimal for any individual. fMRI-guided targeting computes your individual target from your brain's functional connectivity, which may improve precision. This is a key differentiator of SAINT\u00ae.",
          )}
        </>
      ),
    },
    {
      heading: "Functional Connectivity",
      body: (
        <>
          {P(
            "Functional connectivity refers to how different brain regions communicate \u2014 which areas activate together at rest. SAINT\u00ae uses resting-state fMRI to measure functional connectivity between your prefrontal cortex and deeper mood circuits, particularly the subgenual anterior cingulate cortex, a region involved in mood regulation.",
          )}
        </>
      ),
    },
    {
      heading: "Personalized Targets",
      body: (
        <>
          {P(
            "Based on your fMRI, a proprietary algorithm selects the stimulation target in the left dorsolateral prefrontal cortex that best connects functionally to the subgenual anterior cingulate cortex. This target is unique to you \u2014 it is not a standard landmark. The algorithm was developed at Stanford and is part of the Magnus Neuromodulation System.",
          )}
        </>
      ),
    },
    {
      heading: "How SAINT\u00ae Incorporates Individualized Targeting",
      body: (
        <>
          {P(
            "Before SAINT\u00ae treatment, you undergo a structural and functional MRI. The algorithm analyzes your brain's functional connectivity and selects your individualized target. The coil is then positioned at that target during each of the 50 sessions over five days. This is why an MRI is required and why people who cannot have an MRI are not candidates. Learn more: [[Does SAINT\u00ae require an MRI?|/learn/does-saint-tms-require-an-mri]].",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT\u00ae TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Difference From Conventional Targeting Approaches",
      body: (
        <>
          {P(
            "Conventional TMS targets the left dorsolateral prefrontal cortex using scalp measurements from the motor-threshold location \u2014 often the '5.5 cm rule' or a similar landmark method. fMRI-guided targeting replaces the ruler with a brain scan and an algorithm that computes your individual target from your functional connectivity. This is the core technical difference between SAINT\u00ae and conventional TMS.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/saint-vs-traditional-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT\u00ae vs traditional TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "How Personalized Brain Targeting Works",
      body: (
        <>
          {P("The process:")}
          <ol className="mt-4 space-y-2">
            <li className="relative pl-6 leading-relaxed text-foreground">
              <span className="absolute left-0 top-0 font-bold text-accent">
                1.
              </span>
              You undergo a structural and functional MRI before treatment.
            </li>
            <li className="relative pl-6 leading-relaxed text-foreground">
              <span className="absolute left-0 top-0 font-bold text-accent">
                2.
              </span>
              The algorithm analyzes your brain's functional connectivity.
            </li>
            <li className="relative pl-6 leading-relaxed text-foreground">
              <span className="absolute left-0 top-0 font-bold text-accent">
                3.
              </span>
              It selects the target in your prefrontal cortex that best connects
              to mood circuits.
            </li>
            <li className="relative pl-6 leading-relaxed text-foreground">
              <span className="absolute left-0 top-0 font-bold text-accent">
                4.
              </span>
              The coil is positioned at your individualized target during each
              session.
            </li>
          </ol>
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/fmri-guided-tms-for-depression#webpage`,
        name: "What Is fMRI-Guided TMS for Depression?",
        url: `${SITE_URL}/learn/fmri-guided-tms-for-depression`,
        description:
          "fMRI-guided TMS: what fMRI is, why targeting matters, functional connectivity, personalized targets, and how SAINT® uses individualized targeting.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/fmri-guided-tms-for-depression#faq`),
      breadcrumbJsonld(baseCrumbs("fMRI-Guided TMS")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "What Is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description: "Protocol, FDA clearance, and evidence",
    },
    {
      title: "SAINT® vs Traditional TMS",
      href: "/learn/saint-vs-traditional-tms",
      description: "Targeting, schedule, and technology",
    },
    {
      title: "BEAM F3 vs fMRI targeting",
      href: "/learn/beam-f3-vs-fmri-tms",
      description:
        "How head-measurement targeting differs from an fMRI-guided target",
    },
  ],
};
